import type { RecordLikeProtocol, ViewIndependentData } from '@lighthouse/core'
import { type DataSourceAbstract, DataSourceType, type FieldADTValue, type Pagination, type TableColumns } from '@lighthouse/core'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { atomWithImmer } from 'jotai-immer'
import { multiply } from 'rambda'
import React, { startTransition, useMemo, useRef } from 'react'
import { useLatest, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { activeFieldAffectCell, type RecordPoolMap, selectionState } from '../../utils'
import type { TablePopoverState, TableTooltipState } from './TableContent'
import TableRecord from './TableRecord'

interface TableBodyProps {
    dataSourceInfo: DataSourceAbstract
    viewIndependentData: ViewIndependentData
    dataSourceList: DataSourceAbstract[]
    /** recordPoolAtom 使用 Map 结构 */
    recordPoolAtom: ReturnType<typeof atomWithImmer<RecordPoolMap>>
    selectedRecords?: string[]
    tableProps: TableColumns
    scrollElement: HTMLDivElement | null
    pagination: Pagination
    primaryDataSourceFieldIds?: Set<string>
    disableEdit?: boolean
    disableSelect?: boolean
    disableCreateRecord?: boolean
    onRecordSelect?: (recordIds: string[]) => void
    onOpenEditor?: (data: TablePopoverState) => void
    onOpenTooltip?: (data: TableTooltipState) => void
    onLoadMoreData?: (pagination: Pagination, search?: string) => Promise<RecordLikeProtocol[]>
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
}

const SCxTableContainer = styled.div``

const SCxTableBody = styled.div`
    display: flex;
    width: max-content;
    min-width: 100%;
    border-bottom: 1px solid var(--color-gray-200);
    border-radius: 8px;
    background: var(--color-white);

    &:last-child {
        border-bottom: none;
    }
`

const TableBody: React.FC<TableBodyProps> = ({
    dataSourceInfo,
    viewIndependentData,
    dataSourceList,
    recordPoolAtom,
    selectedRecords,
    // schema,
    tableProps,
    scrollElement,
    pagination,
    primaryDataSourceFieldIds,
    disableEdit,
    disableSelect,
    disableCreateRecord,
    onRecordSelect,
    onOpenEditor,
    onOpenTooltip,
    onLoadMoreData,
    onCellChange
}) => {
    const { type, schema } = dataSourceInfo
    const enableAddRecord = type !== DataSourceType.joinDataSource
    // const [loading, setLoading] = useState(false)
    const loadingRef = useRef(false)
    const { currentPage, rowTotal, recordPoints } = viewIndependentData
    const { pageSize } = pagination || ({})
    const pageNumRef = useRef(currentPage)
    const currentPageRef = useLatest(currentPage)
    const visibleTableProps = useMemo(() => tableProps.filter(item => item.visible && schema[item.id]), [schema, tableProps])
    const rowVirtualizer = useVirtualizer({
        count: recordPoints?.length ?? 0,
        scrollPaddingStart: enableAddRecord && !disableCreateRecord ? 77 : 37,
        getScrollElement: () => scrollElement,
        estimateSize: () => 40,
        getItemKey: (index: number) => recordPoints[index].id,
        overscan: 30
    })
    const maxWidth = useMemo(() => {
        return visibleTableProps.reduce((prev, cur) => {
            return prev + (cur.width || 160)
        }, 60)
    }, [visibleTableProps])

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: visibleTableProps.length,
        getScrollElement: () => scrollElement,
        paddingStart: 60,
        estimateSize: (index: number) => visibleTableProps[index].width || 160,
        overscan: 14
    })

    // useMounted(() => {
    //     setScrollElement(scrollRef.current)
    // })

    useUpdateEffect(() => {
        if (currentPage < pageNumRef.current) {
            pageNumRef.current = currentPage
        }
    }, [currentPage])

    useUpdateEffect(() => {
        startTransition(() => {
            columnVirtualizer.measure()
        })
    }, [visibleTableProps])

    useUpdateEffect(() => {
        void (async () => {
            if (!rowTotal || loadingRef.current || !rowVirtualizer.range) {
                return
            }
            const { endIndex } = rowVirtualizer.range
            const checkedId = selectionState.state.fieldCheckId
            activeFieldAffectCell(scrollElement, checkedId, 'data-field', 'data-cell-field')
            const currentCount = multiply(currentPageRef.current, 100)
            // tableFieldSelection.onSelectField(scrollRef.current, id, tableFieldSelection.fieldId)
            if (
                endIndex >= currentCount - 10 &&
                rowTotal > currentCount &&
                onLoadMoreData &&
                pageNumRef.current === currentPageRef.current
            ) {
                loadingRef.current = true
                const recordsData = await onLoadMoreData({ currentPage: currentPageRef.current + 1, pageSize: 100 })

                pageNumRef.current = currentPageRef.current + 1
                if (recordsData.length > 0) {
                    loadingRef.current = false
                }
            }
        })()
    }, [currentPage, onLoadMoreData, rowVirtualizer.range])

    useUpdateEffect(() => {
        rowVirtualizer.measure()
    }, [rowTotal])

    return (
        <SCxTableContainer
            style={{
                width: `${maxWidth}px`,
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: 'relative'
            }}
        >
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
                const { index } = virtualRow
                const { id } = recordPoints[index]
                // const { id, content } = item
                return (
                    <TableRecord
                        key={id}
                        index={index}
                        id={id}
                        recordPoolAtom={recordPoolAtom}
                        dataSourceInfo={dataSourceInfo}
                        dataSourceList={dataSourceList}
                        disableEdit={disableEdit}
                        // content={content}
                        selectedRecords={selectedRecords}
                        tableProps={visibleTableProps}
                        virtualRow={virtualRow}
                        columnVirtualizer={columnVirtualizer}
                        primaryDataSourceFieldIds={primaryDataSourceFieldIds}
                        disableSelect={disableSelect}
                        onRecordSelect={onRecordSelect}
                        onOpenEditor={onOpenEditor}
                        onOpenTooltip={onOpenTooltip}
                        onCellChange={onCellChange}
                    />
                )
            })}
        </SCxTableContainer>
    )
}

export default TableBody
